import { Box, Card, CircularProgress, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { FleetSafetyScores } from '../../../../backendsdk';
import { TrackedLink as Link } from '../../../../components/TrackedComponents';
import IssScore from '../../../../images/csa/iss.svg';
import palette from '../../../ColorPalette';
import CsaScoreCard from './CsaScoreCard';
import CsaScoreExplanationCard from './CsaScoreExplanationCard';
import CsaScoreHistoryCard from './CsaScoreHistoryCard';

const SCORE_CARDS = [
    'unsafe_driving_score',
    'crash_indicator_score',
    'hos_compliance_score',
    'vehicle_maintenance_score',
    'controlled_substances_alcohol_score',
    'hazardous_materials_score',
    'driver_fitness_score',
];

interface CsaScoresComponentProps {
    csaScores: FleetSafetyScores[];
    isLoading: boolean;
    isError: boolean;
    onReload: CallableFunction;
}

const CsaScoresComponent: React.FC<CsaScoresComponentProps> = (props: CsaScoresComponentProps) => {
    const [selectedScore, setSelectedScore] = React.useState<string | null>(null);
    const { t } = useTranslation();

    if (props.isError) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                }}
            >
                <Typography variant="overline">Error loading CSA scores</Typography>
                <Link
                    id="try-again-link"
                    component="button"
                    variant="overline"
                    onClick={() => {
                        props.onReload();
                    }}
                    sx={{ lineHeight: 1 }}
                >
                    {t('content.metrics.try_again')}
                </Link>
            </Box>
        );
    } else if (props.isLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    mt: 1,
                }}
            >
                <CircularProgress />
            </Box>
        );
    } else if (props.csaScores.length === 0) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                }}
            >
                <Typography variant="overline">No CSA scores found</Typography>
            </Box>
        );
    }

    const sortedScores = props.csaScores.sort((a, b) => {
        const aDate = dayjs(a.date);
        const bDate = dayjs(b.date);
        if (aDate.isAfter(bDate)) {
            return -1;
        }
        if (aDate.isBefore(bDate)) {
            return 1;
        }
        return 0;
    });
    const latestScore = sortedScores[0];
    const isIssSelected = selectedScore === 'iss_score';

    let historicalData: { score: number; date: string }[] = [];
    if (selectedScore) {
        historicalData = sortedScores
            .map((score) => {
                return {
                    score: score[selectedScore as keyof FleetSafetyScores] as number,
                    date: score.date,
                };
            })
            .reverse();
    }

    return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ width: '100%', display: 'flex', gap: 2 }}>
                {SCORE_CARDS.map((scoreCard) => {
                    return (
                        <CsaScoreCard
                            key={scoreCard}
                            type={scoreCard}
                            value={latestScore[scoreCard as keyof FleetSafetyScores] as number}
                            isSelected={selectedScore === scoreCard}
                            onSelectScore={() => setSelectedScore(scoreCard)}
                        />
                    );
                })}
            </Box>
            <Card
                id="iss-score-card"
                data-testid="iss-score-card"
                onClick={() => setSelectedScore('iss_score')}
                sx={{
                    width: '100%',
                    height: '35px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    py: 2,
                    px: 1,
                    backgroundColor: isIssSelected ? palette.white : palette.neutral[200],
                    '&:hover': {
                        backgroundColor: isIssSelected ? palette.white : palette.neutral[150],
                    },
                    transition: 'background-color 0.2s',
                    userSelect: 'none',
                    cursor: 'pointer',
                    my: 2,
                }}
            >
                <img src={IssScore} alt="ISS Score" />
                <Typography variant="overline" sx={{ fontSize: 14, mt: 0.15, ml: 1, mr: 0.5 }}>
                    ISS Score
                </Typography>
                <Typography
                    data-testid="iss-score-value"
                    data-value={latestScore.iss_score}
                    sx={{ fontWeight: 'bold' }}
                >
                    {latestScore.iss_score}
                </Typography>
            </Card>
            {selectedScore ? (
                <Box sx={{ width: '100%', display: 'flex' }}>
                    <CsaScoreExplanationCard type={selectedScore} />
                    <CsaScoreHistoryCard type={selectedScore} data={historicalData} />
                </Box>
            ) : null}
        </Box>
    );
};

export default CsaScoresComponent;
